import Profile from '@/models/storePanel/settings/Profile';
import { cloneDeep } from 'lodash';

const getDefaultState = () => ({
  loading: {
    logo: false,
    mapLogo: false,
    businessInformation: false,
    invoicing: false,
    operationHours: false,
    fastPayment: false,
    shoppingCart: false,
    changePassword: false,
    redemption: false,
    deliverySettings: false,
    coupon_claim_valid_days: false,
    cashRegisters: false,
    store_settings: false,
    elorus: false,
  },
  errorMessage: {
    logo: '',
    mapLogo: '',
    businessInformation: '',
    invoicing: '',
    operationHours: '',
    fastPayment: '',
    shoppingCart: '',
    changePassword: '',
    redemption: '',
    deliverySettings: '',
    coupon_claim_valid_days: '',
    cashRegisters: '',
    store_settings: '',
  },
  resetSuccess: {
    logo: false,
    mapLogo: false,
    businessInformation: false,
    changePassword: false,
    cashRegisters: false,
    coupon_claim_valid_days: false,
    store_settings: false,
  },
  countries: [],
  orders_allowed: false,
  cashRegisters: [],
  cashRegistersPagination: {},
});

export default {
  namespaced: true,

  state: () => getDefaultState(),

  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
    setLoading(state, { value, type }) {
      state.loading[type] = value;
    },

    setErrorMessage(state, { value, type }) {
      state.errorMessage[type] = value;
    },

    setResetSuccess(state, { value, type }) {
      state.resetSuccess[type] = value;
    },

    setCountries(state, payload) {
      state.countries = payload;
    },

    setCashRegisters(state, payload) {
      state.cashRegisters = payload;
    },

    setCashRegistersPagination(state, payload) {
      state.cashRegistersPagination = payload;
    },
  },

  actions: {
    async uploadLogo({ commit }, { type, imageFile }) {
      try {
        commit('setLoading', { value: true, type });

        const fd = new FormData();
        fd.append('logo', imageFile);

        const { data } = await Profile.uploadImage(fd);

        commit('storePanel/setLogo', data.data.logo, { root: true });
        commit('setResetSuccess', { value: true, type });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated store logo!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async uploadMapLogo({ commit }, { type, imageFile }) {
      try {
        commit('setLoading', { value: true, type });

        const fd = new FormData();
        fd.append('map_logo', imageFile);

        const { data } = await Profile.uploadImage(fd);

        commit('storePanel/setMapLogo', data.data.map_logo, {
          root: true,
        });
        commit('setResetSuccess', { value: true, type });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated map logo!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async uploadOfflineMapLogo({ commit }, { type, imageFile }) {
      try {
        commit('setLoading', { value: true, type });

        const fd = new FormData();
        fd.append('offline_map_logo', imageFile);

        const { data } = await Profile.uploadImage(fd);

        commit('storePanel/setOfflineMapLogo', data.data.offline_map_logo, {
          root: true,
        });
        commit('setResetSuccess', { value: false, type });

        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated Pin Display - Outside Operating Hours!',
          },

          { root: true }
        );
        commit('setLoading', { value: false, type });
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async getCountries({ commit }) {
      try {
        const { data } = await Profile.getCountries();

        commit('setCountries', data.data);
      } catch (ex) {
        console.error(ex.response.data.message);
      }
    },

    async updateBusinessInformation({ commit }, { type, item }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateBusinessInformation(item);

        commit('storePanel/setBusinessInformation', item, {
          root: true,
        });
        commit('setResetSuccess', { value: true, type });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated business information!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'error',
            text: ex.response.data.message,
          },

          { root: true }
        );
      }
    },

    async updateTimetable({ commit, rootState }) {
      try {
        commit('setLoading', { value: true, type: 'operationHours' });

        const timetable = cloneDeep(rootState.storePanel.store.timetable);

        timetable.forEach((day) => {
          if (day.type === 'regular') {
            day.shifts.pop();
          } else if (day.type === '24h' || day.type === 'closed') {
            delete day.shifts;
          }
        });

        const { data } = await Profile.updateTimetable({ timetable });

        commit('storePanel/setTimetable', data.data.timetable, {
          root: true,
        });

        commit('setLoading', { value: false, type: 'operationHours' });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated hours of operations!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type: 'operationHours' });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type: 'operationHours',
        });
        setTimeout(
          () =>
            commit('setErrorMessage', {
              value: '',
              type: 'operationHours',
            }),
          5000
        );
      }
    },

    async updateInvoicing({ commit }, { type, item }) {
      try {
        commit('setLoading', { value: true, type });

        const res = await Profile.updateInvoicing(item);
        console.log(res.data.data.billing_details, 'res.data.data.billing_details');
        commit('storePanel/setInvoicing', res.data.data.billing_details, { root: true });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated invoicing information!',
          },

          { root: true }
        );
        return res.data;
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response?.data?.message || ex.message || ex,
          type,
        });
        console.error(ex);
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
        return ex.response?.data || ex;
      }
    },

    async updateFastPayment({ commit }, { type, fast_payment }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateFastPayment({ fast_payment });

        commit('storePanel/setFastPayment', fast_payment, {
          root: true,
        });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated fast payment!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updateShoppingCart({ commit }, { type, shopping_cart }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateShoppingCart({ shopping_cart });

        commit('storePanel/setShoppingCart', shopping_cart, {
          root: true,
        });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated shopping cart!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updateThermalPrinter({ commit }, { type, thermal_printer }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateThermalPrinter({ thermal_printer });

        commit('storePanel/setThermalPrinter', thermal_printer, {
          root: true,
        });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated thermal printer!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updatePassword({ commit }, { type, item }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updatePassword(item);

        commit('setResetSuccess', { value: true, type });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated password!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updateRedemption({ commit }, { type, redemption_type_id }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateRedemption({ redemption_type_id });

        commit('storePanel/setRedemption', redemption_type_id, {
          root: true,
        });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated redemption settings!',
          },

          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updateDeliverySettings({ commit }, { type, item }) {
      try {
        commit('setLoading', true, { root: true });

        await Profile.updateDeliverySettings(item);

        commit('storePanel/setDeliverySettings', item, {
          root: true,
        });
        commit('setLoading', false, { root: true });
        commit('setDialog', false, { root: true });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated delivery settings!',
          },
          { root: true }
        );
      } catch (ex) {
        commit('setLoading', false, { root: true });
        commit('setErrorMessage', {
          value: ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async updateCouponClaimValidDays({ commit, dispatch }, { type, body }) {
      try {
        commit('setLoading', { value: true, type });

        await Profile.updateStoreCouponClaimValidDays(body);

        commit('setResetSuccess', { value: false, type });
        commit('setLoading', { value: false, type });
        // // dispatch("getBussinessProfile")
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated Coupon Claim Valid Days!',
          },
          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response && ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async getCashRegisters({ commit }) {
      try {
        commit('setLoading', { value: true, type: 'cashRegisters' });
        const { data } = await Profile.getCashRegisters();

        commit('setCashRegisters', data.data.data);
        commit('setCashRegistersPagination', data.data.pagination);
        commit('setLoading', { value: false, type: 'cashRegisters' });
      } catch (ex) {
        commit('setLoading', { value: false, type: 'cashRegisters' });
        console.error(ex.response.data.message);
      }
    },

    async createCashRegister({ commit, dispatch }, item) {
      try {
        commit('setLoading', { value: true, type: 'cashRegisters' });
        const { data } = await Profile.createCashRegister(item);
        await dispatch('getCashRegisters');
        commit('setLoading', { value: false, type: 'cashRegisters' });
      } catch (ex) {
        commit('setLoading', { value: false, type: 'cashRegisters' });
        if (ex.response.data.error) {
          commit(
            'setNotification',
            {
              show: true,
              type: 'error',
              text: ex.response.data.message,
            },

            { root: true }
          );
        }
        console.error(ex.response.data.message);
      }
    },

    async updateCashRegister({ commit, dispatch }, item) {
      try {
        commit('setLoading', { value: true, type: 'cashRegisters' });
        const { data } = await Profile.updateCashRegister(item.cash_register_id, {
          friendly_name: item.friendly_name,
          cash_register_code: item.cash_register_code,
        });
        await dispatch('getCashRegisters');
        commit('setLoading', { value: false, type: 'cashRegisters' });
      } catch (ex) {
        commit('setLoading', { value: false, type: 'cashRegisters' });
        console.error(ex.response.data.message);
        if (ex.response.data.error) {
          commit(
            'setNotification',
            {
              show: true,
              type: 'error',
              text: ex.response.data.message,
            },

            { root: true }
          );
        }
      }
    },

    async deleteCashRegister({ commit, dispatch }, item) {
      try {
        commit('setLoading', { value: true, type: 'cashRegisters' });
        const { data } = await Profile.deleteCashRegister(item.cash_register_id);
        await dispatch('getCashRegisters');
        commit('setLoading', { value: false, type: 'cashRegisters' });
      } catch (ex) {
        commit('setLoading', { value: false, type: 'cashRegisters' });
        console.error(ex.response.data.message);
      }
    },
    async updateTransactionLimit({ commit, dispatch }, { type, body }) {
      try {
        commit('setLoading', { value: true, type });
        if (!body.min_amount_limit || !body.max_amount_limit) {
          body = {
            min_amount_limit: body.min_amount_limit || 0,
            max_amount_limit: body.max_amount_limit || 0,
          };
        }
        await Profile.updateTransactionLimit(body);

        commit('setResetSuccess', { value: false, type });
        commit('setLoading', { value: false, type });
        commit(
          'setNotification',
          {
            show: true,
            type: 'success',
            text: 'You have successfully updated Transaction Limit!',
          },
          { root: true }
        );
      } catch (ex) {
        commit('setLoading', { value: false, type });
        commit('setErrorMessage', {
          value: ex.response && ex.response.data.message,
          type,
        });
        setTimeout(() => commit('setErrorMessage', { value: '', type }), 5000);
      }
    },

    async createElorusClientId({ commit, dispatch }, clientId) {
      try {
        commit('setLoading', { value: true, type: 'elorus' });

        const { data } = await Profile.createElorusClientId(clientId);

        commit('setLoading', { value: false, type: 'elorus' });
        return data;
      } catch (ex) {
        commit('setLoading', { value: false, type: 'elorus' });

        setTimeout(() => commit('setErrorMessage', { value: '', type: 'invoicing' }), 5000);
        return ex.response?.data || ex;
      }
    },
  },
};
